import React from 'react';
import { Link } from 'react-router-dom';

const About: React.FC = () => {
  return (
    <div className="space-y-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">About Triple Point Trading Strategy</h1>
        <p className="text-lg text-gray-700 dark:text-gray-100">
          The Triple Point Trading Strategy combines three powerful analytical methods to identify high-probability investment opportunities while minimizing risk.
        </p>
      </div>

      {/* Strategy Overview */}
      <section>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Strategy Overview</h2>
        <p className="mb-4 text-gray-700 dark:text-gray-300">
          The Triple Point approach finds investment opportunities where three key metrics converge ("triple point"), 
          providing stronger signals than any single metric alone:
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          <div className="card border-t-4 border-primary-500">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">1. Valuation (CAPE)</h3>
            <p className="text-gray-700 dark:text-gray-300">
              Cyclically Adjusted Price-to-Earnings ratio helps identify whether a stock is undervalued or overvalued
              relative to its historical earnings, adjusting for economic cycles.
            </p>
          </div>

          <div className="card border-t-4 border-primary-500">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">2. Financial Health (F-Score)</h3>
            <p className="text-gray-700 dark:text-gray-300">
              Piotroski F-Score evaluates nine aspects of a company's financial strength across profitability, 
              leverage/liquidity, and operating efficiency metrics.
            </p>
          </div>

          <div className="card border-t-4 border-primary-500">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">3. Technical Trends (P&F)</h3>
            <p className="text-gray-700 dark:text-gray-300">
              Point & Figure charting identifies significant price movements while filtering out market noise,
              providing clear breakout and breakdown signals.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">How The Strategy Works</h2>
        
        <div className="card mb-6">
          <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-3">Buy Signals</h3>
          <p className="mb-2 text-gray-700 dark:text-gray-300">A buy signal is generated when:</p>
          <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
            <li>Low CAPE percentile (stock is relatively undervalued)</li>
            <li>High F-Score (strong financial health, 7-9 range)</li>
            <li>P&F chart shows a bullish pattern (breakout)</li>
          </ul>
        </div>

        <div className="card mb-6">
          <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-3">Sell Signals</h3>
          <p className="mb-2 text-gray-700 dark:text-gray-300">A sell signal is generated when:</p>
          <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
            <li>High CAPE percentile (stock is relatively overvalued)</li>
            <li>Low F-Score (deteriorating financial health, 0-3 range)</li>
            <li>P&F chart shows a bearish pattern (breakdown)</li>
          </ul>
        </div>

        <div className="card">
          <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-3">Hold Signals</h3>
          <p className="mb-2 text-gray-700 dark:text-gray-300">A hold signal is generated when:</p>
          <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
            <li>Mixed or neutral indicators</li>
            <li>Moderate F-Score (4-6 range)</li>
            <li>Unclear technical patterns</li>
          </ul>
        </div>
      </section>

      {/* Application Features */}
      <section>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Application Features</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="card">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">Dashboard</h3>
            <p className="text-gray-700 dark:text-gray-300">
              A comprehensive overview of all current signals across the S&P 500, with at-a-glance metrics showing buy, sell, and hold recommendations.
            </p>
            <div className="mt-4">
              <Link to="/" className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 font-medium">
                View Dashboard →
              </Link>
            </div>
          </div>

          <div className="card">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">Stock Analysis</h3>
            <p className="text-gray-700 dark:text-gray-300">
              Detailed analysis page for individual stocks, showing CAPE scores, F-Score breakdowns, and interactive P&F charts.
            </p>
            <div className="mt-4">
              <Link to="/stock-analysis" className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 font-medium">
                Analyze Stocks →
              </Link>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">Portfolio Management</h3>
            <p className="text-gray-700 dark:text-gray-300">
              Track your portfolio performance, add positions, and monitor real-time updates against strategy signals.
            </p>
            <div className="mt-4">
              <Link to="/portfolio" className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 font-medium">
                Manage Portfolio →
              </Link>
            </div>
          </div>

          <div className="card">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">Strategy Configuration</h3>
            <p className="text-gray-700 dark:text-gray-300">
              Customize strategy parameters to fit your risk tolerance and investment goals.
            </p>
            <div className="mt-4">
              <Link to="/strategy-configuration" className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 font-medium">
                Configure Strategy →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Financial Disclaimer */}
      <section id="disclaimer" className="bg-red-50 dark:bg-red-900 p-6 rounded-lg border-l-4 border-red-400 dark:border-red-600 mt-12">
        <h2 className="text-2xl font-bold text-red-800 dark:text-red-200 mb-4">Financial Disclaimer</h2>
        <div className="text-red-700 dark:text-red-300 space-y-4">
          <p className="font-medium">
            The information provided in this application is for educational and research purposes only.
          </p>
          <p>
            Triple Point Trading Strategy does not provide financial advice, and nothing contained within this application 
            should be construed as investment, financial, legal, or tax advice. All content, signals, analyses, and recommendations 
            are presented without warranty of any kind.
          </p>
          <p>
            Trading and investing in financial markets involves substantial risk of loss. Past performance 
            is not indicative of future results. The signals and analyses generated by this application are based on 
            historical data and theoretical models, which may not accurately predict future market movements.
          </p>
          <p>
            Users should conduct their own research and consult with qualified financial professionals before 
            making any investment decisions. The creators and operators of this application are not responsible 
            for any losses incurred as a result of using the information or signals provided.
          </p>
          <p className="font-medium">
            By using this application, you acknowledge that you are solely responsible for your investment 
            decisions and accept all risks associated with such decisions.
          </p>
        </div>
      </section>
    </div>
  );
};

export default About; 